import React, { useState, useEffect, useRef } from "react";
import { Box, ToggleButtonGroup, ToggleButton, Typography } from "@mui/material";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
const API_URL = import.meta.env.VITE_API_URL || "http://localhost:3000";

// Fix default marker icons
delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
    iconRetinaUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png",
    iconUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png",
    shadowUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png",
});

const TILE_LAYERS: Record<string, { url: string; attribution: string }> = {
    street: {
        url: "https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png",
        attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>',
    },
    satellite: {
        url: "https://mt1.google.com/vt/lyrs=y&x={x}&y={y}&z={z}",
        attribution: "&copy; Google",
    },
};

// Decode OSRM polyline (Google Polyline Algorithm)
function decodePolyline(encoded: string): [number, number][] {
    const points: [number, number][] = [];
    let index = 0, lat = 0, lng = 0;
    while (index < encoded.length) {
        let b, shift = 0, result = 0;
        do { b = encoded.charCodeAt(index++) - 63; result |= (b & 0x1f) << shift; shift += 5; } while (b >= 0x20);
        lat += (result & 1) ? ~(result >> 1) : (result >> 1);
        shift = 0; result = 0;
        do { b = encoded.charCodeAt(index++) - 63; result |= (b & 0x1f) << shift; shift += 5; } while (b >= 0x20);
        lng += (result & 1) ? ~(result >> 1) : (result >> 1);
        points.push([lat / 1e5, lng / 1e5]);
    }
    return points;
}

const createCircleIcon = (color: string, label: string) =>
    L.divIcon({
        className: "custom-marker",
        html: `<div style="
      width:24px;height:24px;border-radius:50%;
      background:${color};color:#fff;
      display:flex;align-items:center;justify-content:center;
      font-size:0.6rem;font-weight:800;
      border:2px solid #fff;
      box-shadow:0 2px 6px rgba(0,0,0,0.3);
    ">${label}</div>`,
        iconSize: [24, 24],
        iconAnchor: [12, 12],
    });

interface Location {
    id?: string;
    name: string;
    address: string;
    latitude?: number;
    longitude?: number;
    location_type: "pickup" | "dropoff" | "driver_change";
    status?: string;
    sequence_order?: number;
}

interface RouteMapProps {
    locations: Location[];
    height?: number | string;
}

const navy = "#0F1B2D";
const amberDark = "#B45309";

const RouteMap: React.FC<RouteMapProps> = ({ locations, height = 200 }) => {
    const [layer, setLayer] = useState<"street" | "satellite">("street");
    const mapRef = useRef<L.Map | null>(null);
    const containerRef = useRef<HTMLDivElement>(null);
    const tileRef = useRef<L.TileLayer | null>(null);
    const routeLineRef = useRef<L.Polyline | null>(null);

    const geoLocations = locations.filter(
        (l) => l.latitude && l.longitude && !isNaN(Number(l.latitude)) && !isNaN(Number(l.longitude))
    );

    // Initialize map
    useEffect(() => {
        if (!containerRef.current || geoLocations.length === 0) return;
        if (mapRef.current) return;

        const bounds = L.latLngBounds(
            geoLocations.map((l) => [Number(l.latitude!), Number(l.longitude!)] as [number, number])
        );

        const map = L.map(containerRef.current, {
            zoomControl: false,
            scrollWheelZoom: false,
        }).fitBounds(bounds, { padding: [25, 25] });

        const tile = TILE_LAYERS.street;
        tileRef.current = L.tileLayer(tile.url, { attribution: tile.attribution }).addTo(map);

        // Sort locations by sequence
        const sorted = [...geoLocations].sort((a, b) => (a.sequence_order || 0) - (b.sequence_order || 0));
        const straightLinePoints: [number, number][] = sorted.map((l) => [Number(l.latitude!), Number(l.longitude!)]);

        // Draw straight-line fallback first (dashed)
        const fallbackLine = L.polyline(straightLinePoints, {
            color: navy, weight: 3, opacity: 0.5, dashArray: "8, 6",
        }).addTo(map);
        routeLineRef.current = fallbackLine;

        // Fetch OSRM real road geometry
        if (sorted.length >= 2) {
            const coords = sorted.map(l => `${l.longitude},${l.latitude}`).join(";");
            const url = `${API_URL}/api/shipments/osrm-proxy/${coords}?overview=full&geometries=polyline`;

            fetch(url)
                .then(res => res.json())
                .then(data => {
                    if (data.code === "Ok" && data.routes?.[0]?.geometry) {
                        const decoded = decodePolyline(data.routes[0].geometry);
                        // Remove fallback line, add real road route
                        if (routeLineRef.current) {
                            map.removeLayer(routeLineRef.current);
                        }
                        routeLineRef.current = L.polyline(decoded, {
                            color: navy, weight: 4, opacity: 0.85,
                        }).addTo(map);
                        // Fit bounds to the road route
                        map.fitBounds(routeLineRef.current.getBounds(), { padding: [25, 25] });
                    }
                })
                .catch(() => { /* keep fallback */ });
        }

        // Markers
        sorted.forEach((loc, idx) => {
            const isPickup = loc.location_type === "pickup";
            const isDriverChange = loc.location_type === "driver_change";
            const isCompleted = loc.status === "completed";
            const color = isCompleted ? "#10B981" : isDriverChange ? "#7C3AED" : isPickup ? navy : amberDark;

            L.marker([Number(loc.latitude!), Number(loc.longitude!)], {
                icon: createCircleIcon(color, String(idx + 1)),
            })
                .bindPopup(`<strong>${loc.name}</strong><br/><span style="color:#666">${loc.address}</span>`)
                .addTo(map);
        });

        mapRef.current = map;

        return () => {
            map.remove();
            mapRef.current = null;
        };
    }, [geoLocations.length]);

    // Switch tile layer
    useEffect(() => {
        if (!mapRef.current || !tileRef.current) return;
        const tile = TILE_LAYERS[layer];
        tileRef.current.setUrl(tile.url);
    }, [layer]);

    if (geoLocations.length === 0) {
        return (
            <Box sx={{
                height, display: "flex", alignItems: "center", justifyContent: "center",
                bgcolor: "#F8FAFC", borderRadius: "10px", border: "1.5px dashed #CBD5E1",
            }}>
                <Typography variant="body2" color="text.disabled">No map coordinates available</Typography>
            </Box>
        );
    }

    return (
        <Box sx={{ position: "relative", borderRadius: "10px", overflow: "hidden", border: "1px solid #E2E8F0" }}>
            {/* Layer toggle */}
            <Box sx={{
                position: "absolute", top: 6, right: 6, zIndex: 1000,
                bgcolor: "rgba(255,255,255,0.92)", borderRadius: "6px",
                boxShadow: "0 1px 4px rgba(0,0,0,0.12)",
            }}>
                <ToggleButtonGroup
                    value={layer} exclusive
                    onChange={(_, v) => v && setLayer(v)}
                    size="small"
                    sx={{
                        "& .MuiToggleButton-root": {
                            px: 0.8, py: 0.2, fontSize: "0.6rem", fontWeight: 600,
                            textTransform: "none", border: "none",
                            "&.Mui-selected": { bgcolor: navy, color: "#fff" },
                        },
                    }}
                >
                    <ToggleButton value="street">Map</ToggleButton>
                    <ToggleButton value="satellite">Sat</ToggleButton>
                </ToggleButtonGroup>
            </Box>

            <div ref={containerRef} style={{ height: typeof height === "number" ? `${height}px` : height, width: "100%" }} />
        </Box>
    );
};

export default RouteMap;
